import { logger } from '@adornis/base/logging.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import i18next from 'i18next';
import '../../i18n.js';

import {
  extractImageIdsFromExistingFileId,
  persistFileAsImageIds,
  persistRoleArtifact,
  persistUploadsAsFileIds,
  resolveUploadRole,
  uploadLogPrefix,
  type UploadType,
} from './file-intake.js';
import { dispatchSubmissionSplitMutation } from './submission-split.js';
import { isEncryptedPdfError, isUserFacingUploadError, UserFacingUploadError } from './upload-errors.js';

// Re-exported so the existing importers of these keep resolving through this module.
export { isUserFacingUploadError, UserFacingUploadError } from './upload-errors.js';
export type { UploadType } from './file-intake.js';

function notifyUser(message: string): void {
  const maybeAlert = (globalThis as any)?.alert;
  if (typeof maybeAlert === 'function') {
    maybeAlert(message);
    return;
  }
  logger.warn(message);
}

export async function handleImageFileUpload(
  examId: string | null = null,
  uploadType: UploadType = 'submissions',
  fileIds?: string[] | null,
  conversationId?: string | null,
  guestUserID?: string,
): Promise<string[] | boolean | null> {
  /*
  Warning: At the moment all uploaded files are converted to png (if not already) and stored as ImageFiles. Each file is a submission of the current exam.
   */
  logger.info({ examId, uploadType }, 'handleImageFileUpload called');
  try {
    if (!examId) {
      notifyUser(i18next.t('upload.noExamId', 'Keine Klausur-ID gefunden')); // <-- Übersetzt!
      return null;
    }

    const usingFileIds = Array.isArray(fileIds) && fileIds.length > 0;
    const role = resolveUploadRole(uploadType);
    const isSubmissionUpload = role === 'submissions';
    const processUsingOriginalIds = usingFileIds && !isSubmissionUpload;

    let files: any[] = [];

    if (usingFileIds) {
      files = fileIds.slice();
    } else {
      const fileList = await askForFile({
        acceptFileTypes: '.png,.pdf',
        acceptMultipleFiles: true,
      });
      if (!fileList || (typeof (fileList as any).length === 'number' && (fileList as any).length === 0)) {
        notifyUser(i18next.t('upload.noFileSelected', 'Keine Datei ausgewählt')); // <-- Übersetzt!
        return null;
      }

      files = Array.from(fileList);
    }

    // SUBMISSIONS ARE A JOB. Everything that used to happen here — the split agent, the PDF explode and encode
    // per student, the Submission inserts, the confirmation request — is `runSubmissionSplit`, run by the
    // `submissionSplit` row. This path's whole remaining responsibility is to make the work ADDRESSABLE: get the
    // uploads persisted so the payload can be ids, then queue.
    //
    // It returns as soon as the row exists, and both callers are fine with that because neither ever used the
    // submission ids — the client panel checks truthiness to flip "uploading" to "uploaded", and the upload agent
    // discards the result. What they gain is that a restart no longer loses the upload.
    if (isSubmissionUpload) {
      const sourceFileIds = usingFileIds
        ? (files as string[]).map(id => String(id ?? '').trim()).filter(Boolean)
        : await persistUploadsAsFileIds(files as File[], uploadLogPrefix(role));

      if (sourceFileIds.length === 0) {
        notifyUser(i18next.t('upload.processingFailed', 'Datei konnte nicht verarbeitet werden')); // <-- Übersetzt!
        return null;
      }

      await dispatchSubmissionSplitMutation(examId, sourceFileIds, conversationId ?? '', guestUserID ?? '')();
      logger.info({ examId, files: sourceFileIds.length }, 'Queued the submission split');
      return sourceFileIds;
    }

    let uploadedAny = false;

    for (const file of files) {
      if (!file) {
        notifyUser(i18next.t('upload.processingFailed', 'Datei konnte nicht verarbeitet werden')); // <-- Übersetzt!
        return null;
      }

      // A non-submission upload keeps its already-persisted file id; only the split produces fresh File objects,
      // and that now happens inside the job.
      let imageIds: string[];
      if (processUsingOriginalIds) {
        imageIds = await extractImageIdsFromExistingFileId(String(file));
      } else {
        const persisted = await persistFileAsImageIds(file, uploadLogPrefix(role));
        if (!persisted) {
          notifyUser(
            i18next.t('upload.invalidFileType', 'Ungültiger Dateityp. Bitte wählen Sie eine PNG- oder PDF-Datei.'),
          ); // <-- Übersetzt!
          return null;
        }
        imageIds = persisted;
      }

      if (role) {
        await persistRoleArtifact(examId, role, imageIds);
        uploadedAny = true;
      }
    }

    return uploadedAny;
  } catch (error: any) {
    const msg = String(error?.message || error || '').toLowerCase();
    const isUserCancel =
      error?.name === 'AbortError' ||
      msg.includes('no files were selected') ||
      msg.includes('no files') ||
      msg.includes('cancel') ||
      msg.includes('aborted');

    if (isUserCancel) {
      logger.info('User cancelled file selection; aborting upload flow');
      return null;
    }

    if (isUserFacingUploadError(error)) {
      logger.error(error, 'Benutzerrelevanter Uploadfehler:');
      throw error;
    }

    if (isEncryptedPdfError(error)) {
      const userMessage = i18next.t('upload.encryptedPdfError', {
        defaultValue:
          'Eine hochgeladene PDF ist verschlüsselt und konnte nicht verarbeitet werden. Bitte entsperre die PDF (Passwortschutz entfernen) und lade sie erneut hoch.',
      });
      throw new UserFacingUploadError('PDF_ENCRYPTED', userMessage, String(error?.message || error || ''));
    }

    logger.error(
      { err: error, message: error?.message, stack: error?.stack, errorType: typeof error, name: error?.name },
      'Fehler beim Upload',
    );
    notifyUser(i18next.t('upload.uploadError', 'Fehler beim Hochladen der Datei')); // <-- Übersetzt!
    return null;
  }
}
